import { customAlphabet } from "nanoid";

const urls = {
	development: `ws://${location.hostname}:3001`,
	production: "wss://4p1.4m3ric4.com"
};

const WS_URL = urls[process.env.NODE_ENV] || urls.development;

export const ME = (() => {
	const key = "socket-identifier:v6";

	const id =
		localStorage.getItem(key) ||
		`${customAlphabet("ABCDEFGHIJKLMNOPQRSTUVWXYZ", 3)()}-${customAlphabet("012346789", 4)()}`;

	localStorage.setItem(key, id);

	return id;
})();

function createClient(url = WS_URL) {
	let socket = undefined;
	let errorHandlers = [];
	let closeHandlers = [];
	let messageHandlers = [];
	let isConnecting = false;
	let reconnectAttempts = 0;
	let reconnectTimeout;
	let messageQueue = [];
	const MAX_RECONNECT_DELAY = 30000;
	const INITIAL_RECONNECT_DELAY = 1000;

	function onOpen(handler) {
		if (socket && socket.readyState === 1) {
			handler();
			return;
		}

		const onOpenHandler = function didOpenSocket() {
			if (socket && socket.readyState === 1) {
				handler();
			}
			socket.removeEventListener("open", onOpenHandler);
		};

		if (socket) {
			socket.addEventListener("open", onOpenHandler, { once: true });
		}
	}

	function processQueue() {
		if (socket.readyState !== 1) {
			return;
		}

		while (messageQueue.length > 0) {
			const { kind, body, to } = messageQueue.shift();
			sendImmediate(kind, body, to);
		}
	}

	function sendImmediate(kind, body = {}, to) {
		if (!socket || socket.readyState !== 1) {
			return false;
		}

		try {
			const message = { kind, to, ...body, from: ME };
			const data = JSON.stringify(message);
			socket.send(data);
			return true;
		} catch (error) {
			return false;
		}
	}

	function send(kind, body = {}, to) {
		if (!socket || socket.readyState !== 1) {
			messageQueue.push({ kind, body, to });
			connect();
			return false;
		}

		return sendImmediate(kind, body, to);
	}

	function calculateReconnectDelay() {
		// Exponential backoff with jitter
		const delay = Math.min(INITIAL_RECONNECT_DELAY * Math.pow(1.5, reconnectAttempts), MAX_RECONNECT_DELAY);
		return delay * (0.9 + Math.random() * 0.2); // Add jitter ±10%
	}

	function resetConnection() {
		if (socket) {
			socket.onopen = null;
			socket.onclose = null;
			socket.onerror = null;
			socket.onmessage = null;

			try {
				socket.close();
			} catch (e) {
				// Ignore errors on close
			}

			socket = undefined;
		}
	}

	function reattachMessageHandlers() {
		if (socket === undefined) {
			return;
		}

		messageHandlers.forEach((entry) => {
			socket.addEventListener("message", entry.listener);
		});
	}

	function connect() {
		if (isConnecting || (socket && socket.readyState === 1)) {
			return;
		}

		clearTimeout(reconnectTimeout);
		isConnecting = true;
		resetConnection();

		try {
			socket = new WebSocket(url);

			socket.addEventListener("open", function didOpenSocket() {
				reconnectAttempts = 0;
				isConnecting = false;
				sendImmediate("id", { id: ME });
				reattachMessageHandlers();
				processQueue();
			});

			socket.addEventListener("close", function didCloseSocket(event) {
				isConnecting = false;

				closeHandlers.forEach((handler) => {
					handler();
				});

				if (event.code !== 1000) {
					scheduleReconnect();
				}
			});

			socket.addEventListener("error", function didFailSocket(e) {
				isConnecting = false;

				errorHandlers.forEach((handler) => {
					handler(e);
				});
			});
		} catch (error) {
			isConnecting = false;
			scheduleReconnect();
		}
	}

	function scheduleReconnect() {
		if (reconnectTimeout) {
			clearTimeout(reconnectTimeout);
		}

		reconnectAttempts++;
		const delay = calculateReconnectDelay();

		reconnectTimeout = setTimeout(() => {
			connect();
		}, delay);
	}

	function on(kind, handler) {
		if (!socket) {
			connect();
		}

		function didReceiveMessage(e) {
			try {
				const message = JSON.parse(e.data);

				if (message.kind !== kind) {
					return;
				}

				handler(message);
			} catch (error) {
				// Ignore parse errors
			}
		}

		const handlerEntry = { kind, handler, listener: didReceiveMessage };
		messageHandlers.push(handlerEntry);

		if (socket) {
			socket.addEventListener("message", didReceiveMessage);
		}

		return function off() {
			const index = messageHandlers.indexOf(handlerEntry);

			if (index !== -1) {
				messageHandlers.splice(index, 1);
			}

			if (socket) {
				socket.removeEventListener("message", didReceiveMessage);
			}
		};
	}

	function broadcast(kind, body) {
		return send(kind, body, "all");
	}

	function onError(handler) {
		errorHandlers.push(handler);
		return function off() {
			errorHandlers = errorHandlers.filter((h) => h !== handler);
		};
	}

	function onClose(handler) {
		closeHandlers.push(handler);
		return function off() {
			closeHandlers = closeHandlers.filter((h) => h !== handler);
		};
	}

	function close() {
		clearTimeout(reconnectTimeout);
		resetConnection();
		messageQueue = [];
		errorHandlers = [];
		closeHandlers = [];
		messageHandlers = [];
	}

	// Initialize connection
	connect();

	return {
		open: onOpen,
		send,
		on,
		broadcast,
		onError,
		onClose,
		close,
		reconnect: connect
	};
}

const client = createClient();

export default client;
